import React, { useEffect, useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import ClientPageCardHeader from 'ts/common/components/ClientPageCardHeader';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ButtonProps } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { formatDate } from 'ts/common/questionnaire/common';
import type { QuestionnaireContext } from 'client_react/questionnaire/QuestionnaireApp';
import QuestionnaireEmailForm from 'client_react/questionnaire/components/QuestionnaireEmailForm';
import QuestionnaireQuestionsForm, {
    type QuestionnaireFormValues
} from 'client_react/questionnaire/components/QuestionnaireQuestionsForm';

function buildQuestionnaireDefaultValues(
    questionnaire: SpApi.Client.IQuestionnaire
): QuestionnaireFormValues {
    const pages: QuestionnaireFormValues['pages'] = {};

    questionnaire.pages.forEach((page, pageIndex) => {
        const pageKey = String(pageIndex);

        pages[pageKey] = {};

        page.questions.forEach((question) => {
            const questionKey = String(question.id);

            if (question.responseType === 'choices_checkboxes') {
                pages[pageKey][questionKey] = [];
            } else {
                pages[pageKey][questionKey] = question.responseText ?? '';
            }
        });
    });
    return { pages };
}

const QuestionnairePage: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');

    const { questionnaire } = useOutletContext<QuestionnaireContext>();
    const [searchParams, setSearchParams] = useSearchParams();

    // TODO: Get default page index from API based on last page with a response
    const defaultPageIndex = 0;
    let pageIndex = searchParams.has('page')
        ? Number(searchParams.get('page')) - 1
        : defaultPageIndex;

    const sortedPages =
        'pages' in questionnaire
            ? [...questionnaire.pages].sort((a, b) => a.displayOrder - b.displayOrder)
            : undefined;

    if (!sortedPages?.[pageIndex]) {
        pageIndex = defaultPageIndex;
    }

    const defaultValues = useMemo<QuestionnaireFormValues>(() => {
        if ('pages' in questionnaire && questionnaire.pages.length) {
            return buildQuestionnaireDefaultValues(questionnaire);
        }

        return { pages: {} };
    }, [questionnaire]);

    const formMethods = useForm<QuestionnaireFormValues>({
        defaultValues
    });

    useEffect(() => {
        if ('pages' in questionnaire && questionnaire.pages.length) {
            formMethods.reset(buildQuestionnaireDefaultValues(questionnaire));
        }
    }, [questionnaire, formMethods]);

    if (!('pages' in questionnaire)) {
        return <QuestionnaireEmailForm />;
    }

    const buttons: Array<ButtonProps> = [
        ...(pageIndex > 0
            ? [
                  {
                      text: t('previousPage'),
                      variant: 'outline primary' as const,
                      onClick: () => {
                          setSearchParams({ page: String(pageIndex) });
                      }
                  }
              ]
            : []),
        ...(pageIndex < questionnaire.pages.length - 1
            ? [
                  {
                      text: t('nextPage'),
                      variant: 'primary' as const,
                      onClick: () => {
                          setSearchParams({ page: String(pageIndex + 2) });
                      }
                  }
              ]
            : []),
        ...(pageIndex === questionnaire.pages.length - 1
            ? [
                  {
                      text: t('submit'),
                      onClick: () => {
                          // TODO: Submit questionnaire
                      }
                  }
              ]
            : [])
    ];

    return (
        <FormProvider {...formMethods}>
            <Flex direction="column" gap="16px" marginBottom="24px">
                <ClientPageCardHeader
                    headingText={questionnaire.name}
                    statusText={t(`statuses.${questionnaire.questionnaireStatus}`)}
                />
                <Flex
                    as="dl"
                    margin={0}
                    fontSize="14px"
                    flexDirection={{ base: 'column', md: 'row' }}
                    gap={{ base: '24px', md: '48px' }}
                    alignItems={{ base: 'stretch', md: 'flex-start' }}
                >
                    <Flex
                        flexDirection="column"
                        gap="4px"
                        minWidth={{ base: 'auto', md: '200px' }}
                        width={{ base: '100%', md: 'auto' }}
                    >
                        <Text as="dt" fontWeight="600" marginBottom={0}>
                            {t('fromLabel')}
                        </Text>
                        <Text as="dd">{questionnaire.brandName}</Text>
                    </Flex>
                    <Flex
                        flexDirection="column"
                        gap="4px"
                        minWidth={{ base: 'auto', md: '200px' }}
                        width={{ base: '100%', md: 'auto' }}
                    >
                        <Text as="dt" fontWeight="600" marginBottom={0}>
                            {t('toLabel')}
                        </Text>
                        <Text as="dd">{questionnaire.contact.name}</Text>
                    </Flex>
                    <Flex flexDirection="column" gap="4px" width={{ base: '100%', md: 'auto' }}>
                        <Text as="dt" fontWeight="600" marginBottom={0}>
                            {t('dueDateLabel')}
                        </Text>
                        <Text as="dd">
                            {questionnaire.dueDate ? formatDate(questionnaire.dueDate, 'UTC') : '-'}
                        </Text>
                    </Flex>
                </Flex>
                {questionnaire.descriptionHtml && (
                    <Box
                        fontFamily="open-sans"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(questionnaire.descriptionHtml)
                        }}
                    />
                )}
                {sortedPages?.[pageIndex] && (
                    <QuestionnaireQuestionsForm
                        questionnairePage={sortedPages[pageIndex]}
                        pageIndex={pageIndex}
                    />
                )}
                <ResponsiveButtonGroup buttons={buttons} />
            </Flex>
        </FormProvider>
    );
};

export default QuestionnairePage;
